import { OptionsType } from 'cookies-next/lib/types';
import { imageTypes } from '../constants/defineFile';
import imageCompression from 'browser-image-compression';
import { PreviewFile } from '../models/file/fileModel';
import { IndexNotExist } from '../constants/typedef';
import { IMedia, MediaOperation } from '../models/media/IMedia';

const handleFile = async (file: MediaOperation, options?: OptionsType | any) => {
    const data = new FormData();

    const appendFileToData = (file: File) => {
        data.append('file', file);
    };

    if (typeof file === 'string') {
        try {
            const blob: any = await dataURLtoBlob(file);
            const extension = blob.type.split('/')[1];
            let f = new File([blob], `${Date.now()}.${extension}`, {
                type: blob.type,
            });

            if (imageTypes.test(extension)) {
                if (options) {
                    const compressedBlob = await imageCompression(blob, options);
                    const compressedFile = new File([compressedBlob], compressedBlob.name || `${Date.now()}.${extension}`, {
                        type: compressedBlob.type,
                    });
                    appendFileToData(compressedFile);
                } else {
                    appendFileToData(f);
                }
            } else {
                appendFileToData(f);
            }
        } catch (error) {
            console.error('Error processing string file:', error);
        }
    } else if (file instanceof File) {
        try {
            if (file.type.indexOf('image') > IndexNotExist.DEFAULT) {
                if (options) {
                    const compressedBlob = await imageCompression(file, options);
                    const compressedFile = new File([compressedBlob], compressedBlob.name || `${Date.now()}.${file.type.split('/')[1]}`, {
                        type: compressedBlob.type,
                    });
                    appendFileToData(compressedFile);
                } else {
                    appendFileToData(file);
                }
            } else {
                appendFileToData(file);
            }
        } catch (error) {
            console.error('Error processing file object:', error);
        }
    } else if ('mediaType' in file && 'file' in file && file.file) {
        // CreateMedia or DraftMedia
        try {
            if (file.file.type.indexOf('image') > IndexNotExist.DEFAULT) {
                if (options) {
                    const compressedBlob = await imageCompression(file.file, options);
                    const compressedFile = new File([compressedBlob], compressedBlob.name || `${Date.now()}.${file.file.type.split('/')[1]}`, {
                        type: compressedBlob.type,
                    });
                    appendFileToData(compressedFile);
                } else {
                    appendFileToData(file.file);
                }
            } else {
                appendFileToData(file.file);
            }
        } catch (error) {
            console.error('Error processing file object:', error);
        }
    }

    return data;
};

const handleMultipleFiles = async (files: MediaOperation[], options?: OptionsType | any): Promise<FormData[]> => {
    const promises = files.map((file) => handleFile(file, options));
    return Promise.all(promises);
};

function dataURLtoBlob(dataUrl: string): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const req = new XMLHttpRequest();
        req.open('GET', dataUrl);
        req.responseType = 'arraybuffer';
        req.onload = function fileLoaded() {
            const mime = this.getResponseHeader('content-type');
            if (mime) {
                resolve(new Blob([this.response], { type: mime }));
            } else {
                reject(new Error('Failed to get MIME type'));
            }
        };
        req.onerror = function () {
            reject(new Error('Failed to convert data URL to Blob'));
        };
        req.send();
    });
}

function checkFileSizeOver(file: File) {
    const maxSizeInBytes = 20 * 1024 * 1024; // 20MB
    return new Promise((resolve) => {
        if (file.size > maxSizeInBytes) {
            resolve(true);
        } else {
            resolve(false);
        }
    });
}

const processFiles = async (files: IMedia[]): Promise<PreviewFile[]> => {
    const processedFiles: PreviewFile[] = [];
    await Promise.all(
        files.map(async (file) => {
            if (file.mediaUrl && file.mediaUrl.indexOf('blob:') > IndexNotExist.DEFAULT) {
                const newFile = (await fileHelper.handleFile(file)).get('file') as PreviewFile;
                processedFiles.push(newFile);
            } else {
                const newFile = await mapMediaToPreviewFile(file);
                processedFiles.push(newFile);
            }
        })
    );
    return processedFiles;
};

const mapMediaToPreviewFile = (media: IMedia): PreviewFile => {
    const fileData = new Blob([], { type: 'image/*' });
    const file = new File([fileData], media.mediaUrl || '', { type: media.mediaType });
    const previewFile: PreviewFile = file as PreviewFile;
    previewFile.preview = media.mediaUrl;
    return previewFile;
};

const getFileNameAndExtension = (fileName: string) => {
    const lastDotIndex = fileName.lastIndexOf('.');
    const baseName = lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName;
    const extension = lastDotIndex !== -1 ? fileName.slice(lastDotIndex + 1) : '';
    return { baseName, extension };
};

export const fileHelper = {
    handleMultipleFiles,
    handleFile,
    checkFileSizeOver,
    getFileNameAndExtension,
    processFiles,
    mapMediaToPreviewFile,
};
